import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import axios from 'services/axios/axios';
import ADGroupDetailsPanel from './ADGroupDetailsPanel';

const initialCriteria = {
	applicationId: '',
	groupName: '',
	groupDescription: '',
	ouAbbreviation: '',
	ouDescription: '',
	exactMatch: false,
	leftMatch: false,
};

const ADGroupsLookupDialog = ({
	open,
	onClose,
	onSelect,
	id,
}) => {
	const [criteria, setCriteria] = useState(initialCriteria);
	const [applications, setApplications] = useState([]);
	const [results, setResults] = useState([]);
	const [message, setMessage] = useState('');
	const [isLoadingApplications, setIsLoadingApplications] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [expandedGroupId, setExpandedGroupId] = useState(null);
	const [selectedIds, setSelectedIds] = useState([]);

	const hasCriteria = useMemo(() => {
		return (
			criteria.applicationId !== '' ||
			criteria.groupName.trim() !== '' ||
			criteria.groupDescription.trim() !== '' ||
			criteria.ouAbbreviation.trim() !== '' ||
			criteria.ouDescription.trim() !== ''
		);
	}, [criteria]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const loadInitialData = async () => {
			try {
				setIsLoadingApplications(true);
				setMessage('');

				const appsResponse = await axios('/api/ApplicationManagement/GetAll');

				const mappedApps = (appsResponse.data || []).map(app => ({
					id: app.AppMaster?.ID ?? '',
					name: app.AppMaster?.Name ?? '',
				})).sort((a, b) => a.name.localeCompare(b.name));

				setApplications(mappedApps);
			} catch (error) {
				setMessage('Unable to load AD Groups Lookup data.');
			} finally {
				setIsLoadingApplications(false);
			}
		};

		loadInitialData();
	}, [open]);

	const handleCriteriaChange = event => {
		const { name, value, checked, type } = event.target;

		setCriteria(current => ({
			...current,
			[name]: type === 'checkbox' ? checked : value,
			...(name === 'exactMatch' && checked ? { leftMatch: false } : {}),
			...(name === 'leftMatch' && checked ? { exactMatch: false } : {}),
		}));
	};

	const handleSearch = async () => {
		if (!hasCriteria) {
			setMessage('Please enter search criteria!');
			setResults([]);
			return;
		}

		try {
			setIsSearching(true);
			setMessage('');
			setSelectedIds([]);

			const matchMode = criteria.exactMatch ? 1 : criteria.leftMatch ? 2 : 0;

			const response = await axios('/api/ActiveDirectory/ADGroupsLookup', {
				params: {
					GroupName: criteria.groupName || undefined,
					GroupDescription: criteria.groupDescription || undefined,
					OUAbbrv: criteria.ouAbbreviation || undefined,
					OUDesc: criteria.ouDescription || undefined,
					AppID: criteria.applicationId || undefined,
					MatchMode: matchMode,
				},
			});

			const payload = response.data?.payload;
			const data = payload?.Results || [];
			if (payload?.HasMoreResults) {
				setMessage('Results are limited to 100 entries. Please refine your search criteria for more specific results.');
			}

			setResults(data);

			if (!data || !data.length) {
				setMessage('No results were found for the search criteria entered!');
			}
		} catch (error) {
			setResults([]);
			setMessage('An error occurred while searching AD groups.');
		} finally {
			setIsSearching(false);
		}
	};

	const handleClose = () => {
		setCriteria(initialCriteria);
		setResults([]);
		setMessage('');
		setExpandedGroupId(null);
		setSelectedIds([]);
		onClose();
	};

	const handleToggleDetails = id => {
		setExpandedGroupId(current => (current === id ? null : id));
	};

	const handleRowToggle = id => {
		setSelectedIds(current =>
			current.includes(id)
				? current.filter(x => x !== id)
				: [...current, id]
		);
	};

	const getNextSelectedIds = id => {
		const nextSelectedIds = [...selectedIds];
		return nextSelectedIds.includes(id) 
			? nextSelectedIds.filter(x => x !== id) 
			: [...nextSelectedIds, id];
	};

	const allResultIds = results.map(r => r.ID);
	const allSelected = allResultIds.length > 0 && allResultIds.every(id => selectedIds.includes(id));
	const someSelected = allResultIds.some(id => selectedIds.includes(id)) && !allSelected;

	const handleToggleAll = () => {
		setSelectedIds(allSelected ? [] : allResultIds);
	};

	const handleSelectSingle = id => {
		const nextSelectedIds = getNextSelectedIds(id);
		setSelectedIds(nextSelectedIds);
		handleConfirmSelection(nextSelectedIds);
	};

	const handleConfirmSelection = (ids = selectedIds) => {
		const selected = results
			.filter(r => ids.includes(r.ID))
			.map(r => ({
				...r,
				GroupName: r.IsCloudGroup ? `[Azure Cloud] ${r.GroupName}` : r.GroupName,
			}));
		onSelect(selected);
		handleClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth='lg' id={`${id}_dialog`}>
			<DialogTitle>AD Groups Lookup</DialogTitle>

			<DialogContent dividers>
				<Box sx={{ display: 'grid', gap: 2 }}>
					<Typography>
						Use this form to search an AD group based on the search
						criteria. Next, click on &apos;More Information&apos; to
						view more information about the group.
					</Typography>

					{message ? <Alert severity='warning'>{message}</Alert> : null}

					{isLoadingApplications ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : (
						<Box
							component='form'
							onSubmit={e => { e.preventDefault(); handleSearch(); }}
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
								gap: 2,
							}}
						>
							<FormControl fullWidth>
								<InputLabel id='ad-groups-application-label'>
									Application
								</InputLabel>
								<Select
									labelId='ad-groups-application-label'
									label='Application'
									name='applicationId'
									value={criteria.applicationId}
									onChange={handleCriteriaChange}
								>
									<MenuItem value=''>--Select--</MenuItem>
									{applications.map(application => (
										<MenuItem
											key={application.id}
											value={application.id}
										>
											{application.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								label='Group Name'
								name='groupName'
								value={criteria.groupName}
								onChange={handleCriteriaChange}
								fullWidth
							/>

							<TextField
								label='Group Description'
								name='groupDescription'
								value={criteria.groupDescription}
								onChange={handleCriteriaChange}
								fullWidth
							/>

							<TextField
								label='Organizational Unit Abbreviation'
								name='ouAbbreviation'
								value={criteria.ouAbbreviation}
								onChange={handleCriteriaChange}
								fullWidth
							/>

							<TextField
								label='Organizational Unit Description'
								name='ouDescription'
								value={criteria.ouDescription}
								onChange={handleCriteriaChange}
								fullWidth
							/>

							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<FormControlLabel
									control={
										<Checkbox
											name='exactMatch'
											checked={criteria.exactMatch}
											onChange={handleCriteriaChange}
										/>
									}
									label='Exact Match'
								/>
								<FormControlLabel
									control={
										<Checkbox
											name='leftMatch'
											checked={criteria.leftMatch}
											onChange={handleCriteriaChange}
										/>
									}
									label='Left Match'
								/>
							</Box>

							{/* Hidden submit button enables Enter key submission */}
							<button type='submit' hidden disabled={isSearching || isLoadingApplications} />
						</Box>
					)}

					<Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
						<Button
							variant='contained'
							onClick={handleSearch}
							disabled={isSearching || isLoadingApplications}
							id={`${id}_searchbutton`}
						>
							{isSearching ? 'Searching...' : 'Search'}
						</Button>
					</Box>

					{results.length ? (
						<Table size='small' id={`${id}_resultstable`}>
							<TableHead>
								<TableRow>
									<TableCell>
										<Checkbox
											indeterminate={someSelected}
											checked={allSelected}
											onChange={handleToggleAll}
											color='primary'
										/>
									</TableCell>
									<TableCell>Group Name</TableCell>
									<TableCell>Organizational Unit</TableCell>
									<TableCell>Details</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{results.map(result => (
									<>
										<TableRow key={result.ID} hover>
											<TableCell>
												<Checkbox
													checked={selectedIds.includes(result.ID)}
													onChange={() => handleRowToggle(result.ID)}
													color='primary'
												/>
											</TableCell>
											<TableCell>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													{result.IsCloudGroup ? (
														<Chip
															label='Azure Cloud'
															size='small'
															variant='outlined'
															sx={{
																borderColor: 'var(--mdc-theme-primary)',
																color: 'var(--mdc-theme-primary)',
															}}
														/>
													) : null}
													<Link
														component='button'
														type='button'
														onClick={() => handleSelectSingle(result.ID)}
														underline='hover'
													>
														{result.GroupName}
													</Link>
												</Box>
											</TableCell>
											<TableCell>{result.OrganizationalUnit}</TableCell>
											<TableCell>
												<Link
													component='button'
													type='button'
													onClick={() => handleToggleDetails(result.ID)}
													underline='hover'
												>
													{expandedGroupId === result.ID ? 'Hide Details' : 'More Information'}
												</Link>
											</TableCell>
										</TableRow>
										<TableRow key={`${result.ID}-details`}>
											<TableCell colSpan={4} sx={{ py: 0, border: 'none' }}>
												<ADGroupDetailsPanel
													result={result}
													open={expandedGroupId === result.ID}
												/>
											</TableCell>
										</TableRow>
									</>
								))}
							</TableBody>
						</Table>
					) : null}
				</Box>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button
					onClick={() => handleConfirmSelection()}
					disabled={selectedIds.length === 0}
					variant='contained'
					id={`${id}_confirmselectionbutton`}
				>
					Confirm Selection
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ADGroupsLookupDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSelect: PropTypes.func.isRequired,
	onMoreInfo: PropTypes.func,
};

export default ADGroupsLookupDialog;